import { ageGroup1, ageGroup2, ageGroup3, ageGroup4 } from '@/assets/messages';
import { PrimaryAnnotatedType, SecondaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import ToggledBarChart, { BarChartData } from '@/components/bar-chart/toggled';
import { BarChartItem } from '@/components/bar-chart/vertical';
import {
  OnStepEnterExitArgs,
  ScrollGraphic,
  ScrollRoot,
  ScrollStep,
  ScrollSteps,
} from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { I18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useState } from 'react';

enum AgeGroup {
  younger = 'younger',
  older = 'older',
}

enum MediaType {
  digital = 'digital',
  analog = 'analog',
}

function getLabel(i18n: I18n, key: string) {
  switch (key) {
    case AgeGroup.younger:
      return ageGroup1(i18n);
    case AgeGroup.older:
      return ageGroup4(i18n);
    case MediaType.digital:
      return t(i18n)({ message: 'digital sources', id: 'common.digital' });
    case MediaType.analog:
      return t(i18n)({ message: 'traditional sources', id: 'common.analog' });
    default:
      return '';
  }
}

export function GoToSources2023Chapter2({
  digitalVsTraditionalData,
  socialMediaData,
  selectedGeneration = AgeGroup.younger,
}: {
  digitalVsTraditionalData: GoToSourcesData['digital_vs_analog'];
  socialMediaData: GoToSourcesData['use_social_media'];
  selectedGeneration?: AgeGroup;
}) {
  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const chartData: BarChartData[] = [
    {
      id: AgeGroup.younger,
      label: getLabel(i18n, AgeGroup.younger),
      data: [
        {
          value: digitalVsTraditionalData.digital.younger,
          label: getLabel(i18n, MediaType.digital),
        },
        {
          value: digitalVsTraditionalData.analog.younger,
          label: getLabel(i18n, MediaType.analog),
        },
      ],
    },
    {
      id: AgeGroup.older,
      label: getLabel(i18n, AgeGroup.older),
      data: [
        {
          value: digitalVsTraditionalData.digital.older,
          label: getLabel(i18n, MediaType.digital),
        },
        {
          value: digitalVsTraditionalData.analog.older,
          label: getLabel(i18n, MediaType.analog),
        },
      ],
    },
  ];

  const [chart2Visible, setChart2Visible] = useState(false);
  const [chart2Data, setChart2Data] = useState<BarChartItem[]>([]);

  const handleChart2StepEnter = useCallback((e: OnStepEnterExitArgs<BarChartItem[]>) => {
    setChart2Visible(true);
    if (e.data) setChart2Data(e.data);
  }, []);

  return (
    <>
      <ScrollRoot css={{ '--step-height': '150vh' }}>
        <ScrollGraphic>
          <ToggledBarChart
            label={t({
              id: 'go-to-sources.chapter-2.chart-label',
              message: '% who use different sources to get information',
            })}
            data={chartData}
            initialId={selectedGeneration}
            formatValue={formatPercentage}
          />
        </ScrollGraphic>

        <ScrollSteps offset={0.7} hasGraphic>
          <ScrollStep>
            <StoryText>
              <Trans id="go-to-sources-2023.chapter-2.text-1">
                There's a huge contrast between young and older generations in their reliance on{' '}
                <PrimaryAnnotatedType>digital</PrimaryAnnotatedType> vs{' '}
                <SecondaryAnnotatedType>traditional</SecondaryAnnotatedType> information sources.
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>

      <ScrollRoot>
        <ScrollGraphic>
          <HorizontalBarChart
            label={t({
              id: 'go-to-sources-2023.social-chart-label',
              message: '% who mostly use social media platforms to get information',
            })}
            data={chart2Data}
            visible={chart2Visible}
            formatValue={formatPercentage}
          />
        </ScrollGraphic>

        <ScrollSteps onStepEnter={handleChart2StepEnter} offset={0.8}>
          <ScrollStep
            data={[
              {
                label: ageGroup1(i18n),
                value: socialMediaData['15_24'],
              },
              {
                label: ageGroup2(i18n),
                value: socialMediaData['25_39'],
              },
              {
                label: ageGroup3(i18n),
                value: socialMediaData['40_64'],
              },
              {
                label: ageGroup4(i18n),
                value: socialMediaData['65plus'],
              },
            ]}
          >
            <StoryText>
              <Trans id="go-to-sources-2023.chapter-2.text-2">
                The gap is especially wide in generations’ use of social media
              </Trans>
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
